import {
  type ColumnDef,
  PaginationState,
  createColumnHelper,
} from "@tanstack/react-table";
import { SortingState } from "@tanstack/react-table";
import { useDebounce } from "@uidotdev/usehooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { Clickable } from "@design-system/Clickable/Clickable";
import { Table } from "@design-system/Table/Table";
import { Text } from "@design-system/Text/Text";

import { routes } from "@pages/routes";

import { usePaginationWithParams } from "@utils/hooks/usePaginationParams";

import {
  ResourceGroupEmployeesQuery,
  useResourceGroupEmployeesQuery,
} from "./ResourceGroupEmployeesTable.g";

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE_TIME = 400;

type EmployeeNode = NonNullable<
  NonNullable<
    NonNullable<
      ResourceGroupEmployeesQuery["resourceGroupEmployees"]
    >["edges"][number]
  >["node"]
>;

const columnHelper = createColumnHelper<EmployeeNode>();

type Props = {
  clientId: string;
  clientApplicationId: string;
  initialPageIndex?: number;
  search?: string;
};

export const ResourceGroupEmployeesTable = ({
  clientId,
  clientApplicationId,
  initialPageIndex = 0,
  search,
}: Props) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: PAGE_SIZE,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_TIME);

  const employeesQuery = useResourceGroupEmployeesQuery(
    {
      clientApplicationId,
      first: pagination.pageSize,
      offset: pagination.pageIndex * pagination.pageSize,
      orderBy:
        sorting.length > 0
          ? `${sorting[0].desc ? "-" : ""}${sorting[0].id}`
          : undefined,
      // Note: Backend supports OR search across name and email
      // We pass search to name field, backend handles OR logic
      name_Icontains: debouncedSearch || undefined,
    },
    {
      enabled: !!clientApplicationId, // Ensure query only runs with valid ID
    },
  );

  const navigate = useNavigate();
  const handlePaginationChange = usePaginationWithParams(
    pagination,
    setPagination,
  );

  const isEmployeesLoading = employeesQuery.isLoading;
  const isEmployeesFetching = employeesQuery.isFetching;

  useEffect(() => {
    setPagination({ pageIndex: 0, pageSize: PAGE_SIZE });
  }, [debouncedSearch]);

  const data = useMemo(() => {
    if (!employeesQuery.data?.resourceGroupEmployees?.edges) {
      return [];
    }
    return employeesQuery.data.resourceGroupEmployees.edges
      .map((edge) => edge?.node)
      .filter((node): node is NonNullable<typeof node> => !!node);
  }, [employeesQuery.data]);

  const totalCount =
    employeesQuery.data?.resourceGroupEmployees?.totalCount ?? 0;

  const handleEmployeeClick = useCallback(
    (clientEmployeeId: string) => {
      navigate(
        routes.portal.clients.client.clientEmployees.clientEmployee.dashboard.$buildPath(
          {
            params: { clientId, clientEmployeeId },
          },
        ),
        {
          viewTransition: true,
        },
      );
    },
    [clientId, navigate],
  );

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("name", {
          header: "Name",
          id: "name",
          cell: (props) => {
            return (
              <Clickable
                onClick={() => handleEmployeeClick(props.row.original.id)}
                outline="sm"
                stretch
              >
                <Table.UserCell name={props.getValue() || ""} />
              </Clickable>
            );
          },
          footer: undefined,
        }),
        columnHelper.accessor("email", {
          header: "Email",
          id: "email",
          cell: (props) => {
            return <Text truncate>{props.getValue() || ""}</Text>;
          },
          footer: undefined,
        }),
      ] as ColumnDef<EmployeeNode>[],
    [handleEmployeeClick],
  );

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination({ pageIndex: 0, pageSize });
  }, []);

  return (
    <Table
      columns={columns}
      data={data}
      rowCount={totalCount}
      onPaginationChange={handlePaginationChange}
      pagination={pagination}
      isLoading={isEmployeesLoading}
      isFetching={isEmployeesFetching}
      onPageSizeChange={handlePageSizeChange}
      sorting={sorting}
      onSortingChange={setSorting}
    />
  );
};
